import type { ShareType } from 'src/utils/share';
import React from 'react';
import { FullPrayerHub } from '@api/routes/prayer-hubs/get/params';
import cn from 'classnames';
import _ from 'lodash';
import { Link, generatePath, useLocation, useNavigate } from 'react-router-dom';
import Icon from 'src/components/common/Icon';
import VideoJSPlayer from 'src/components/common/VideoJSPlayer';
import { DOMAIN_URL, PRAYER_HUB_SHARE_DESCRIPTION, PRAYER_HUB_SHARE_TITLE, SHARE_CONSTANTS } from 'src/constants/other';
import { PRAYER_HUB_ROUTE, PRAYER_ROUTE } from 'src/constants/routes';
import { useAppSelector, useTranslation } from 'src/hooks';
import { useOnScreen } from 'src/hooks/visible-on-screen';
import { selectLang } from 'src/store/settings/selectors';
import { formatBucketLink } from 'src/utils/format-bucket-link';
import { removeHtmlTags } from 'src/utils/remove-html-tags';
import { share } from 'src/utils/share';

import css from './PrayerHubsFeedItem.module.scss';
import Splash from './Splash';

interface Props {
  className?: string;
  position?: 'prev' | 'current' | 'next';
  isActive?: boolean;
  data?: FullPrayerHub | null;
}

const getMediaLink = (src: string) => {
  if (!src) return;

  return formatBucketLink(src);
};

function HubComponent(props: Props) {
  const { data, position = 'current', isActive, className = '' } = props;
  const isCurrentSlide = isActive ?? position === 'current';
  const [isVideoPlayerActive, setIsVideoPlayerActive] = React.useState(false);
  const autoplayTimoutId = React.useRef<NodeJS.Timeout | null>(null);
  const companyRef = React.useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const lang = useAppSelector(selectLang);
  const { translate: _t_createHub } = useTranslation('createHub');
  const seeMoreLabel = 'קראו עוד...';

  const videoSrc = data?.video ? getMediaLink(data?.video) : '';
  const imgSrc = data?.image ? getMediaLink(data?.image) : '';

  const videoRef = React.useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(videoRef, 1);

  React.useEffect(() => {
    const page = Number(new URLSearchParams(location.search).get('page'));

    setIsVideoPlayerActive(false);

    if (page && isCurrentSlide) {
      autoplayTimoutId.current = setTimeout(() => {
        setIsVideoPlayerActive(true);
      }, 1800);
    }

    return () => {
      if (autoplayTimoutId.current) clearTimeout(autoplayTimoutId.current);
    };
  }, [isCurrentSlide, location.search]);

  const handleSlideClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    if (target.closest('button, a, input, textarea, select, [role="button"]')) {
      return;
    }

    navigate(generatePath(PRAYER_HUB_ROUTE, { id: data?._id as string }));
  };

  const onShareClick = React.useCallback(() => {
    if (!data) return;

    share({
      type: SHARE_CONSTANTS.WHATS_APP as ShareType,
      ogTitle: PRAYER_HUB_SHARE_TITLE.replace(
        '${name}',
        `${data.personName} ${_t_createHub(`buttons.${data.gender}`)} ${data.motherName}`
      ),
      ogDescription: PRAYER_HUB_SHARE_DESCRIPTION,
      url: `${DOMAIN_URL}${PRAYER_HUB_ROUTE.replace(':id', data._id)}`,
    });
  }, [data]);

  if (!data) {
    if (position !== 'current') return null;

    return (
      <div className={cn(css.feedItem, css[position], className)} ref={companyRef}>
        <Splash className={css.splash} />
      </div>
    );
  }

  return (
    <div className={cn(css.feedItem, css[position], !!videoSrc && css.videoPlayerActive, className)} ref={companyRef}>
      <div className={css.mediaClip} onClick={handleSlideClick}>
        <div className={css.mediaFrame}>
          <div className={css.media} ref={videoRef}>
            {!videoSrc && imgSrc && (
              <div
                className={css.bg}
                style={{
                  backgroundImage: `url(${imgSrc})`,
                }}
              />
            )}

            {videoSrc && (
              <VideoJSPlayer
                id={`hub-video-${data._id}`}
                className={css.videoPlayer}
                videoSRC={videoSrc}
                isActive={isVisible}
                isDefaultControls
                isRepeated
              />
            )}

            <div className={css.actions}>
              <button type="button" aria-label="שתף בוואטסאפ" onClick={onShareClick}>
                <Icon type="share" />
              </button>
              {/* <button type="button">
                <Icon type="bookmark" />
              </button>
              <button type="button" className={cn(data.prayerCount != null && css.prayerAction)}>
                <Icon type="praying-hands" />
                {data.prayerCount != null && <span className={css.prayerCount}>{data.prayerCount}</span>}
              </button> */}
            </div>
            <div className={css.info}>
              {data.category && <p className={css.category}>{data.category.title[lang]}</p>}
              <div className={css.titleWrap}>
                <p
                  className={css.title}
                >{`${data.personName} ${_t_createHub(`buttons.${data.gender}`)} ${data.motherName}`}</p>
              </div>
              <p className={cn(css.descriptionPreview)}>
                <span className={css.descriptionText} dangerouslySetInnerHTML={{ __html: data.description }}></span>
                <Link to={generatePath(PRAYER_HUB_ROUTE, { id: data._id })} className={css.seeMoreBtn}>
                  {seeMoreLabel}
                </Link>
              </p>
              <Link to={generatePath(PRAYER_ROUTE, { id: data._id })} className={css.link}>
                חזקו בתפילה
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const PrayerHubsFeedItem = React.memo(HubComponent, (prev, next) => {
  return (
    prev.data?._id === next.data?._id &&
    prev.isActive === next.isActive &&
    prev.className === next.className &&
    prev.position === next.position
  );
});

export default PrayerHubsFeedItem;
